import {
  animateBar,
  columns,
  hideNotations,
  notationPositions,
  notationTypes,
  showNotation,
} from './netZeroChartHelper';

export const averageNonEVEmissions = 174;
export const averageEVEmissions = 55;
export const electricitySoftLimit = 100000;
export const kwhFuelSoftLimit = 100000;
export const litreFuelSoftLimit = 20000;
export const electricityHardLimit = 250000;
export const kwhFuelHardLimit = 250000;
export const litreFuelHardLimit = 50000;
export const mileageHardLimit = 100000;

const elecTonnesCoefficient = 0.0002249893886;
const gasTonnesCoefficient = 0.0001829289262;
const oilTonnesCoefficient = 0.00254015585637584;
const lpgTonnesCoefficient = 0.00155712778389262;

const solarTonnesCoefficient = -1.5;
const elecTonnesCoefficientStep2 = 0.9;
const heatTonnesCoefficientStep2 = 0.9;
const heatTonnesGasCoefficientStep4 = 0.3320805309;
const heatTonnesOilCoefficientStep4 = 0.246165802;
const heatTonnesLPGCoefficientStep4 = 0.2832087022;
const elecTonnesCoefficientStep5 = 0.2000094328;
const heatTonnesGasCoefficientStep5 = 0.073799154;
const heatTonnesOilCoefficientStep5 = 0.0547060916;
const heatTonnesLPGCoefficientStep5 = 0.0629382354;

export const bedroomOptions = {
  ONE: 'one',
  TWO_OR_THREE: 'two-or-three',
  FOUR_PLUS: 'four-plus',
};

export const fuelTypes = {
  ELEC: 'electricity',
  GAS: 'mains-gas',
  OIL: 'oil',
  LPG: 'petroleum',
  PUMP: 'heat-pump',
  RADS: 'electric-radiators',
};

export const energyDefaults = {
  one: {
    electricity: 1800,
    'mains-gas': 8000,
    oil: 800,
    petroleum: 1100,
    'heat-pump': 4200,
    'electric-radiators': 9000,
  },
  'two-or-three': {
    electricity: 2900,
    'mains-gas': 12000,
    oil: 1200,
    petroleum: 1700,
    'heat-pump': 6500,
    'electric-radiators': 13700,
  },
  'four-plus': {
    electricity: 4300,
    'mains-gas': 17000,
    oil: 1700,
    petroleum: 2300,
    'heat-pump': 9400,
    'electric-radiators': 19600,
  },
};

export const energyUnits = {
  LITRES: 'litres',
  KWH: 'kWh',
};

export const getBarData = (tHeight, dataOriginal, dataCurrent) => ({
  electricity: {
    originalTonnes: dataOriginal.electricity,
    currentTonnes: dataCurrent.electricity,
    originalHeight: tHeight * dataOriginal.electricity,
    currentHeight: tHeight * dataCurrent.electricity,
  },
  heating: {
    originalTonnes: dataOriginal.heating,
    currentTonnes: dataCurrent.heating,
    originalHeight: tHeight * dataOriginal.heating,
    currentHeight: tHeight * dataCurrent.heating,
  },
  car: {
    originalTonnes: dataOriginal.car,
    currentTonnes: dataCurrent.car,
    originalHeight: tHeight * dataOriginal.car,
    currentHeight: tHeight * dataCurrent.car,
  },
  solar: {
    originalTonnes: dataOriginal.solar,
    currentTonnes: dataCurrent.solar,
    originalHeight: tHeight * dataOriginal.solar,
    currentHeight: tHeight * dataCurrent.solar,
  },
});

const getHeatingTonnes = (heatingUsage, heatingMethod) => {
  switch (heatingMethod) {
    default:
      return 0;
    case 'mains-gas':
      return heatingUsage * gasTonnesCoefficient;
    case 'oil':
      return heatingUsage * oilTonnesCoefficient;
    case 'petroleum':
      return heatingUsage * lpgTonnesCoefficient;
  }
};

const getHeatingCoefficient = (heatingMethod, stepNumber) => {
  let coefficient;
  switch (stepNumber) {
    default:
      coefficient = heatTonnesCoefficientStep2;
      break;
    case 4:
      if (heatingMethod === 'mains-gas') {
        coefficient = heatTonnesGasCoefficientStep4;
      } else if (heatingMethod === 'oil') {
        coefficient = heatTonnesOilCoefficientStep4;
      } else if (heatingMethod === 'petroleum') {
        coefficient = heatTonnesLPGCoefficientStep4;
      } else {
        coefficient = 0;
      }
      break;
    case 5:
      if (heatingMethod === 'mains-gas') {
        coefficient = heatTonnesGasCoefficientStep5;
      } else if (heatingMethod === 'oil') {
        coefficient = heatTonnesOilCoefficientStep5;
      } else if (heatingMethod === 'petroleum') {
        coefficient = heatTonnesLPGCoefficientStep5;
      } else {
        coefficient = 0;
      }
      break;
  }
  return coefficient;
};

const getCarEmissionsInTonnes = (cars, step) => {
  let tonnes = 0;
  cars.forEach(car => {
    if (step > 1) {
      tonnes += getAnnualCarEmissions(
        'ELECTRICITY',
        car.annualMileage,
        car.details.co2Emissions,
      );
    } else {
      tonnes += getAnnualCarEmissions(
        car.details.fuelType,
        car.annualMileage,
        car.details.co2Emissions,
      );
    }
  });
  if (step === 5) {
    tonnes *= elecTonnesCoefficientStep5;
  }
  return tonnes;
};

const getCarEmissionsInTonnesForInteractive = (cars, forceEV) => {
  let tonnes = 0;
  cars.forEach(car => {
    if (forceEV) {
      tonnes +=
        getAnnualCarEmissions(
          'ELECTRICITY',
          car.annualMileage,
          car.details.co2Emissions,
        ) * elecTonnesCoefficientStep5;
    } else {
      let annualCarEmissions = getAnnualCarEmissions(
        car.details.fuelType,
        car.annualMileage,
        car.details.co2Emissions,
      );
      if (car.details.fuelType === 'ELECTRICITY') {
        annualCarEmissions *= elecTonnesCoefficientStep5;
      }
      tonnes += annualCarEmissions;
    }
  });
  return tonnes;
};

export const getBreakdownSteps = (heatingMethod, usage, cars) => {
  const { electricityUsage, heatingUsage } = usage;
  const electricityTonnesStep1 = elecTonnesCoefficient * electricityUsage;
  const electricityTonnesStep2 =
    electricityTonnesStep1 * elecTonnesCoefficientStep2;
  const electricityTonnesStep5 =
    electricityTonnesStep1 * elecTonnesCoefficientStep5;

  const heatingTonnesStep1 = getHeatingTonnes(heatingUsage, heatingMethod);
  const heatingTonnesStep2 =
    heatingTonnesStep1 * getHeatingCoefficient(heatingMethod);
  const heatingTonnesStep4 =
    heatingTonnesStep1 * getHeatingCoefficient(heatingMethod, 4);
  const heatingTonnesStep5 =
    heatingTonnesStep1 * getHeatingCoefficient(heatingMethod, 5);

  const carTonnesStep1 = getCarEmissionsInTonnes(cars, 1);
  const carTonnesStep3 = getCarEmissionsInTonnes(cars, 3);
  const carTonnesStep5 = getCarEmissionsInTonnes(cars, 5);

  const solarTonnesStep1 = 0;
  const solarTonnesStep2 = solarTonnesCoefficient;

  const breakdownSteps = {
    1: {
      electricity: electricityTonnesStep1,
      heating: heatingTonnesStep1,
      car: carTonnesStep1,
      solar: solarTonnesStep1,
      total: electricityTonnesStep1 + heatingTonnesStep1 + carTonnesStep1,
    },
    2: {
      electricity: electricityTonnesStep1,
      heating: heatingTonnesStep1,
      car: carTonnesStep1,
      solar: solarTonnesStep2,
      total:
        electricityTonnesStep1 +
        heatingTonnesStep1 +
        carTonnesStep1 +
        solarTonnesStep2,
    },
    3: {
      electricity: electricityTonnesStep2,
      heating: heatingTonnesStep2,
      car: carTonnesStep1,
      solar: solarTonnesStep2,
      total:
        electricityTonnesStep2 +
        heatingTonnesStep2 +
        carTonnesStep1 +
        solarTonnesStep2,
    },
    4: {
      electricity: electricityTonnesStep2,
      heating: heatingTonnesStep2,
      car: carTonnesStep3,
      solar: solarTonnesStep2,
      total:
        electricityTonnesStep2 +
        heatingTonnesStep2 +
        carTonnesStep3 +
        solarTonnesStep2,
    },
    5: {
      electricity: electricityTonnesStep2,
      heating: heatingTonnesStep4,
      car: carTonnesStep3,
      solar: solarTonnesStep2,
      total:
        electricityTonnesStep2 +
        heatingTonnesStep4 +
        carTonnesStep3 +
        solarTonnesStep2,
    },
    6: {
      electricity: electricityTonnesStep5,
      heating: heatingTonnesStep5,
      car: carTonnesStep5,
      solar: solarTonnesStep2,
      total:
        electricityTonnesStep5 +
        heatingTonnesStep5 +
        carTonnesStep5 +
        solarTonnesStep2,
    },
  };
  return breakdownSteps;
};

export const getInteractiveBreakdown = (
  heatingMethod,
  usage,
  cars,
  annualMileage,
  useLowCarbonHeat,
  useSolarArray,
  useElectricVehicle,
) => {
  const { electricityUsage, heatingUsage } = usage;

  const unAdjustedHeatingTonnes =
    getHeatingTonnes(heatingUsage, heatingMethod) *
    getHeatingCoefficient(heatingMethod, 2);
  const lowCarbonHeatingTonnes =
    unAdjustedHeatingTonnes * getHeatingCoefficient(heatingMethod, 5);

  const carTonnesNoEV = getCarEmissionsInTonnesForInteractive(cars, false);
  const carTonnesEV = getCarEmissionsInTonnesForInteractive(cars, true);

  const solarTonnesNoSolar = 0;
  const solarTonnesWithSolar = solarTonnesCoefficient;

  const mileageProportion =
    cars.length === 0 ? 0 : annualMileage / getTotalMileage(cars);

  const electricityTonnes =
    elecTonnesCoefficient * electricityUsage * elecTonnesCoefficientStep5;
  const heatingTonnes = !useLowCarbonHeat
    ? unAdjustedHeatingTonnes
    : lowCarbonHeatingTonnes;
  const carTonnes =
    (!useElectricVehicle ? carTonnesNoEV : carTonnesEV) * mileageProportion;
  const solarTonnes = !useSolarArray
    ? solarTonnesNoSolar
    : solarTonnesWithSolar;

  return {
    electricity: electricityTonnes,
    heating: heatingTonnes,
    car: carTonnes,
    solar: solarTonnes,
    total: electricityTonnes + heatingTonnes + carTonnes + solarTonnes,
  };
};

export const animateBars = (barData, zeroY) => {
  animateBar('elec-bar', barData.electricity, zeroY);
  !!barData.heating.originalTonnes &&
    animateBar('heat-bar', barData.heating, zeroY);
  !!barData.car.originalTonnes && animateBar('car-bar', barData.car, zeroY);
  animateBar('solar-bar', barData.solar, zeroY);
};

export const navigateBreakdownSteps = (
  currentStep,
  barData,
  zeroY,
  wrapperRef,
  heatingMethod,
  cars,
) => {
  switch (currentStep) {
    default:
      animateBars(barData, zeroY);
      hideNotations([
        'solar-notation',
        'elec-notation',
        'heat-notation',
        'car-notation',
      ]);
      break;
    case 2:
      animateBars(barData, zeroY);
      hideNotations(['elec-notation', 'heat-notation', 'car-notation']);
      showNotation(
        'solar-notation',
        zeroY,
        columns.fourth,
        wrapperRef.current.offsetWidth,
        notationPositions.above,
        notationTypes.solar,
        heatingMethod,
        cars,
      );
      break;
    case 3:
      animateBars(barData, zeroY);
      hideNotations(['solar-notation', 'car-notation']);
      showNotation(
        'elec-notation',
        zeroY,
        columns.first,
        wrapperRef.current.offsetWidth,
        notationPositions.below,
        notationTypes.elec,
        heatingMethod,
        cars,
      );
      !!barData.heating.originalTonnes &&
        showNotation(
          'heat-notation',
          zeroY,
          columns.second,
          wrapperRef.current.offsetWidth,
          notationPositions.below,
          notationTypes.heat,
          heatingMethod,
          cars,
        );
      break;
    case 4:
      animateBars(barData, zeroY);
      hideNotations(['solar-notation', 'elec-notation', 'heat-notation']);
      !!barData.car.originalTonnes &&
        showNotation(
          'car-notation',
          zeroY,
          columns.third,
          wrapperRef.current.offsetWidth,
          notationPositions.below,
          notationTypes.car,
          heatingMethod,
          cars,
        );
      break;
    case 5:
      animateBars(barData, zeroY);
      hideNotations(['solar-notation', 'elec-notation', 'car-notation']);
      !!barData.heating.originalTonnes &&
        showNotation(
          'heat-notation',
          zeroY,
          columns.second,
          wrapperRef.current.offsetWidth,
          notationPositions.below,
          notationTypes.grid,
          heatingMethod,
          cars,
        );
      break;
    case 6:
      animateBars(barData, zeroY);
      hideNotations([
        'solar-notation',
        'elec-notation',
        'car-notation',
        'heat-notation',
      ]);
      break;
  }
};

export const getAnnualCarEmissions = (
  fuelType,
  annualMileage,
  co2Emissions,
) => {
  if (fuelType === 'ELECTRICITY') {
    return parseFloat(
      ((annualMileage * 1.61 * averageEVEmissions) / 1000 / 1000).toFixed(2),
    );
  } else {
    return parseFloat(
      ((annualMileage * 1.61 * co2Emissions) / 1000 / 1000).toFixed(2),
    );
  }
};

export const getCarEmissions = (fuelType, co2Emissions) => {
  if (fuelType === 'ELECTRICITY') {
    return Math.round(averageEVEmissions * 1.61);
  } else {
    return Math.round(co2Emissions * 1.61);
  }
};

export const getTotalMileage = cars => {
  let mileage = 0;
  cars.forEach(car => {
    mileage += parseInt(car.annualMileage);
  });
  return mileage;
};

export const getHeatingMethodName = heatingMethod => {
  switch (heatingMethod) {
    default:
      return fuelTypes.ELEC;
    case fuelTypes.GAS:
      return 'Gas';
    case fuelTypes.LPG:
      return 'LPG';
    case fuelTypes.OIL:
      return 'Oil';
  }
};

export const getHeatingUnitOfMeasure = heatingMethod => {
  if (heatingMethod === fuelTypes.LPG || heatingMethod === fuelTypes.OIL) {
    return energyUnits.LITRES;
  }
  return energyUnits.KWH;
};

export const getEnergyDefault = (
  bedroomOption,
  heatingMethod,
  isElecDefault,
) => {
  if (
    isElecDefault &&
    (heatingMethod === fuelTypes.RADS || heatingMethod === fuelTypes.PUMP)
  ) {
    return (
      energyDefaults[bedroomOption][fuelTypes.ELEC] +
      energyDefaults[bedroomOption][heatingMethod]
    );
  }
  if (isElecDefault) {
    return energyDefaults[bedroomOption][fuelTypes.ELEC];
  }
  return energyDefaults[bedroomOption][heatingMethod];
};

export const areCarsEV = cars => {
  let allEv = true;

  cars.forEach(car => {
    if (car.details.fuelType !== 'ELECTRICITY') {
      allEv = false;
    }
  });
  return allEv;
};
